import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, Save, HardDrive } from "lucide-react";
import { Button, StatusPill, Select } from "@/components/kit";
import { Txt } from "@/components/Txt";

const LABEL_WORDS = { id: "ID", api: "API", json: "JSON", oauth: "OAuth" };
const prettyField = (f) =>
  f.split("_").map((w) => LABEL_WORDS[w] || w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

function ConnectionCard({ conn, onSaved }) {
  const [values, setValues] = useState({ ...conn.values });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    // Fill select fields (e.g. environment) with their first option so the value
    // shown is the value saved (fixes the environment dropdown not persisting).
    const v = { ...conn.values };
    Object.entries(conn.select_fields || {}).forEach(([f, opts]) => { if (!v[f] && opts?.length) v[f] = opts[0]; });
    setValues(v); setDirty(false);
  }, [conn]);

  const [testing, setTesting] = useState(false);

  const testConn = async () => {
    setTesting(true);
    try {
      const r = await api.post("/square/test-connection");
      if (r.data.ok) toast.success(`Connected to ${r.data.location_name || "Square"} (${r.data.environment})`);
      else toast.error(r.data.error || "Test failed");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const save = async () => {
    // Switching to Production needs a typed confirmation.
    if (conn.service === "square" && values.environment === "production"
        && conn.values.environment !== "production") {
      const typed = window.prompt('Type LIVE to switch Square to Production (real cards will be charged):');
      if ((typed || "").trim().toUpperCase() !== "LIVE") { toast.error("Production not confirmed"); return; }
    }
    try {
      await api.post("/connections", { service: conn.service, values });
      toast.success(`${conn.label} saved`);
      onSaved();
    } catch (e) { toast.error("Could not save"); }
  };

  const connectDrive = async () => {
    try { const r = await api.get("/drive/connect"); window.location.href = r.data.authorization_url; }
    catch (e) { toast.error("Only a Super Admin can connect Google Drive."); }
  };
  const disconnectDrive = async () => {
    try { await api.post("/drive/disconnect"); toast.success("Google Drive disconnected"); onSaved(); }
    catch (e) { toast.error("Could not disconnect"); }
  };

  const statusPill = (
    <StatusPill tone={conn.connected ? "olive" : "gray"} className="gap-1.5 text-[13px] px-2.5 py-1" data-testid={`connection-status-${conn.service}`}>
      {conn.connected ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
      {conn.connected ? <Txt id="connections.status_connected" screen="Connections">Connected</Txt> : <Txt id="connections.status_not_connected" screen="Connections">Not connected</Txt>}
    </StatusPill>
  );

  const Header = (
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-zilla text-xl font-semibold">{conn.label}</h3>
      {statusPill}
    </div>
  );

  if (conn.kind === "oauth") {
    return (
      <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid={`connection-${conn.service}`}>
        {Header}
        {conn.note && <p className="text-[14px] text-ink/65 mb-3">{conn.note}</p>}
        {conn.connected ? (
          <div className="space-y-3">
            {conn.account_email && <div className="text-[13px] text-dark-olive flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> {conn.account_email}</div>}
            <Button variant="danger" data-testid={`connection-disconnect-${conn.service}`} onClick={disconnectDrive}>Disconnect</Button>
          </div>
        ) : (
          <Button data-testid={`connection-connect-${conn.service}`} onClick={connectDrive}><HardDrive className="w-4 h-4" /> Connect Google Drive</Button>
        )}
      </div>
    );
  }

  if (conn.kind === "managed") {
    return (
      <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid={`connection-${conn.service}`}>
        {Header}
        {conn.note && <p className="text-[14px] text-ink/65">{conn.note}</p>}
      </div>
    );
  }

  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid={`connection-${conn.service}`}>
      {Header}
      <div className="space-y-3">
        {conn.fields.map((f) => {
          const isSecret = conn.secret_fields.includes(f);
          const options = (conn.select_fields || {})[f];
          return (
            <div key={f}>
              <label className="text-[13px] font-semibold text-ink/70">{prettyField(f)}</label>
              {options ? (
                <Select
                  data-testid={`connection-input-${conn.service}-${f}`}
                  value={values[f] || options[0]}
                  onChange={(e) => { setValues({ ...values, [f]: e.target.value }); setDirty(true); }}
                  className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] mt-1 capitalize"
                >
                  {options.map((o) => <option key={o} value={o}>{o}</option>)}
                </Select>
              ) : (
                <input
                  data-testid={`connection-input-${conn.service}-${f}`}
                  type={isSecret ? "password" : "text"}
                  value={values[f] || ""}
                  placeholder={isSecret && conn.values[f] ? conn.values[f] : ""}
                  onChange={(e) => { setValues({ ...values, [f]: e.target.value }); setDirty(true); }}
                  className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] mt-1"
                />
              )}
              {f === "environment" && <p className="text-[12px] text-ink/45 mt-1">Use “sandbox” to test, “production” to accept real payments.</p>}
              {isSecret && <p className="text-[12px] text-ink/45 mt-1"><Txt id="connections.secret_note" screen="Connections">Stored securely — shown masked once saved.</Txt></p>}
            </div>
          );
        })}
      </div>
      <Button data-testid={`connection-save-${conn.service}`} onClick={save} disabled={!dirty} className="mt-4">
        <Save className="w-4 h-4" /> Save {conn.label}
      </Button>
      {conn.service === "square" && (
        <div className="mt-4 pt-4 border-t border-ink/10" data-testid="square-extra">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill tone={values.environment === "production" ? "maroon" : "gray"} className="text-[12px] px-2.5 py-1">
              {values.environment === "production" ? "LIVE" : "Sandbox"}
            </StatusPill>
            <Button variant="secondary" data-testid="square-test-btn" onClick={testConn} disabled={testing || dirty}>
              {testing ? "Testing…" : "Test connection"}
            </Button>
          </div>
          {dirty && <p className="text-[12px] text-ink/45 mt-2">Save your changes before testing.</p>}
          {conn.last_test_at && (
            <p className="text-[12px] mt-2" data-testid="square-last-test">
              {conn.last_test_ok
                ? <span className="text-dark-olive">Last successful test: {new Date(conn.last_test_at).toLocaleString("en-US", { timeZone: "America/Chicago" })}</span>
                : <span className="text-maroon">Last test failed: {conn.last_test_error}</span>}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function ConnectionsPage() {
  const [connections, setConnections] = useState([]);
  const load = useCallback(async () => {
    const c = await api.get("/connections");
    setConnections(c.data.connections);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const st = params.get("drive_connected");
    if (st) {
      if (st === "success") { toast.success("Google Drive connected"); load(); }
      else toast.error("Could not connect Google Drive");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [load]);

  return (
    <div>
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-1"><Txt id="connections.title" screen="Connections">Connections</Txt></h1>
      <p className="text-[17px] text-ink/70 mb-5"><Txt id="connections.subtitle" screen="Connections">Connect the outside services Rosemoor relies on — once, here.</Txt></p>
      <div className="bg-gold/15 border border-gold/40 rounded-lg p-4 mb-5 text-[15px] text-ink/80">
        <Txt id="connections.banner" screen="Connections">Enter each service's keys once. Nothing processes payments or sends email yet — that arrives with the modules that use them. Secrets are stored securely and never shown in full again.</Txt>
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
        {connections.map((c) => <ConnectionCard key={c.service} conn={c} onSaved={load} />)}
      </div>
    </div>
  );
}
